import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Grid, Button } from '@material-ui/core';
import { useFormPagamentoValidation } from './form-pagamento-validation';
import { CircularLoading } from 'views/components/utils/circular-loading/circular-loading';
import {
  TextFieldSaurus,

} from 'views/components/controles/inputs';
import {
  DefaultFormProps,
  DefaultFormRefs,
} from 'views/components/form/utils/form-default-props';
import { useThemeQueries, makeUtilClasses } from 'views';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SelectSaurus } from 'views/components/controles/selects/select-saurus/select-saurus';
import { TpComunicacaoMock } from 'data/mocks';
import { InstituicaoPagamentoMock } from 'data/mocks/instituicao-pagamento-mock';
import { EnumInstituicao } from 'model/enums/enum-instituicao';
import { AccordionSaurus } from 'views/components/accordions/accordion-saurus/accordion-saurus';
import { EnumTipoComunicacao } from 'model';
import { isEmpty } from 'lodash';
import { PagamentoFormModel } from 'model/app/forms/pagamento/pagamento-form-model';
import { useCredenciaisPagamento } from 'services/app/use-cases/credenciais-pagamento';
import { CredencialIcon } from 'views/components/icons/credencial-icon';

export const FormPagamentoCadastro = forwardRef<
  DefaultFormRefs<PagamentoFormModel>,
  DefaultFormProps<PagamentoFormModel>
>(
  (
    { loading, onSubmit, ...props }: DefaultFormProps<PagamentoFormModel>,
    ref,
  ) => {
    const { isMobile } = useThemeQueries();
    const utilClasses = makeUtilClasses();
    const refInputNome = useRef<HTMLInputElement>(null);

    const [instituicao, setInstituicao] = useState<EnumInstituicao>(EnumInstituicao.Nenhuma)
    const [tipoComunicacao, setTipoComunicacao] = useState<EnumTipoComunicacao>(EnumTipoComunicacao.Nenhuma)

    const [accordion, setAccordion] = useState<boolean>(false)

    const submitAlterarDadosConta = (values: PagamentoFormModel) => {
      if(isCustomizadas){
        values.credenciais = credenciaisCustomizadas
      }
      onSubmit(values);
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        handleSubmit(submitAlterarDadosConta)();
      },
      resetForm: () => {
        reset();
        if (!isMobile) refInputNome.current?.focus();
      },
      fillForm: (model: PagamentoFormModel) => {
        model.instituicao = EnumInstituicao.Itau
        reset({ ...model });
        if (!isMobile) refInputNome.current?.focus();
      },
    }));

    const { FormPagamentoValidationYup } = useFormPagamentoValidation({
      instituicao: instituicao,
      tipo: tipoComunicacao
    })

    const {
      handleSubmit,
      control,
      formState: { errors, touchedFields },
      reset,
      getValues,
      setValue,
      getFieldState,
      clearErrors
    } = useForm<PagamentoFormModel>({
      resolver: yupResolver(FormPagamentoValidationYup),
      criteriaMode: 'all',
      mode: 'onBlur' && 'onChange',
    });

    //Caso tenham novas formas de pagamento credenciadas, adicionar os campos dentro do hook
    const { getCampos, setCredenciais, habilitarDesabilitarCustomizadas, isCustomizadas, credenciaisCustomizadas } = useCredenciaisPagamento({
      control,
      getFieldState,
      setValue,
      clearErrors,
      getValues,
      loading,
    })

    useEffect(() => {
      setCredenciais(instituicao, tipoComunicacao)
    }, [instituicao, setCredenciais, tipoComunicacao])

    const accordionCredenciais = () => {
      const campos = getCampos(instituicao, tipoComunicacao)

      if (isEmpty(campos)) {
        return null
      }

      return (<AccordionSaurus
        labelPrimary='Credenciais'
        expanded={accordion}
        onChange={() => setAccordion(prev => !prev)}
      >
        <Grid container spacing={2}>
          {campos}
        </Grid>
      </AccordionSaurus>
      )
    }

    return (
      <>
        <Box my={2}>
          <div className={utilClasses.formContainer}>
            {loading && props.showLoading ? (
              <div className={utilClasses.controlLoading}>
                <CircularLoading tipo="NORMAL" />
              </div>
            ) : null}

            <form
              onSubmit={handleSubmit(submitAlterarDadosConta)}
              className={loading ? utilClasses.controlLoading : ''}
            >
              <Box my={3}>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12}>
                    <Controller
                      name="nome"
                      control={control}
                      render={({ field }) => (
                        <TextFieldSaurus
                          inputRef={refInputNome}
                          disabled={loading}
                          allowSubmit={false}
                          id="nome"
                          label="Nome da Forma de Pagamento"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            touchedFields.nome && errors.nome
                              ? errors.nome.message
                              : undefined
                          }
                          error={Boolean(errors.nome && errors.nome.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="tipoComunicacao"
                      control={control}
                      render={({ field }) => (
                        <SelectSaurus
                          variant='outlined'
                          conteudo={TpComunicacaoMock}
                          disabled={loading}
                          label="Tipo de Comunicação"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            touchedFields.tipoComunicacao && errors.tipoComunicacao
                              ? errors.tipoComunicacao.message
                              : undefined
                          }
                          error={Boolean(
                            errors.tipoComunicacao && errors.tipoComunicacao.message,
                          )}
                          {...field}
                          value={getValues('tipoComunicacao')}
                          onChange={ev => {
                            const item = TpComunicacaoMock.filter(item => item.Key === ev.target.value)
                            setValue('tipoComunicacao', item[0].Key)
                            setTipoComunicacao(item[0].Key)
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="instituicao"
                      control={control}
                      render={({ field }) => (
                        <SelectSaurus
                          variant='outlined'
                          conteudo={InstituicaoPagamentoMock}
                          disabled={loading}
                          label="Instituição"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            touchedFields.instituicao && errors.instituicao
                              ? errors.instituicao.message
                              : undefined
                          }
                          error={Boolean(
                            errors.instituicao && errors.instituicao.message,
                          )}
                          {...field}
                          value={getValues('instituicao')}
                          onChange={ev => {
                            const item = InstituicaoPagamentoMock.filter(item => item.Key === ev.target.value)
                            if (item) {
                              setValue('instituicao', item[0].Key)
                              setInstituicao(item[0].Key)
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {accordionCredenciais()}
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant='outlined' color='primary' onClick={() => {
                      habilitarDesabilitarCustomizadas()
                      if(!accordion) setAccordion(true)
                    }} fullWidth>
                      <CredencialIcon tipo='BUTTON'/>
                      {isCustomizadas ? 'Desabilitar Customizadas' : 'Credenciais Customizadas'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              <Button style={{ display: 'none' }} type="submit"></Button>
            </form>
          </div>
        </Box>
      </>
    );
  },
);
